import {
  createShapePropsMigrationIds,
  createShapePropsMigrationSequence,
  StyleProp,
  type TLBaseBinding,
  type TLBaseShape,
} from "@tldraw/tlschema";
/**
 * The dim shape: a linear dimension carrying Bear's dim vocabulary. Each
 * endpoint is either a `dim` binding to a target shape (the arrow pattern
 * generalized) or the free point stored in props. Points in props are
 * absolute page-space coordinates (Y-down); the shape's own x/y stay 0.
 */
import { T } from "@tldraw/validate";

export type DimDirection = "x" | "y" | "aligned";

/**
 * What a dimension endpoint measures to, shown by its terminator: tick =
 * face of stud (the default), arrow = face of finish, dot = a construction
 * line the drafter draws and describes (typically a centerline). Per end,
 * like real legends ("stud to face of finish" mixes a tick and an arrow),
 * as two styles so the tool remembers the last choice for each end.
 */
export const DimRefStartStyle = StyleProp.defineEnum("sketch:dimRefStart", {
  defaultValue: "stud",
  values: ["stud", "finish", "construction"],
});
export const DimRefEndStyle = StyleProp.defineEnum("sketch:dimRefEnd", {
  defaultValue: "stud",
  values: ["stud", "finish", "construction"],
});
export type DimRef = T.TypeOf<typeof DimRefStartStyle>;

export interface DimProps {
  /** Authored Measurement string, or null for a reference dim. */
  value: string | null;
  direction: DimDirection;
  /** Perpendicular offset from the measured span to the dim line, page
   *  units. Null stacks the dim outside the drawing (see dim-stack.ts). */
  offset: number | null;
  /** Chain identity; links sharing it form a Bear <DimChain>. */
  chain: string | null;
  /** Reference each end measures to; decides that end's terminator glyph. */
  refStart: DimRef;
  refEnd: DimRef;
  start: { x: number; y: number };
  end: { x: number; y: number };
}

declare module "@tldraw/tlschema" {
  interface TLGlobalShapePropsMap {
    dim: DimProps;
  }
}

export type DimShape = TLBaseShape<"dim", DimProps>;

const point = T.object({ x: T.number, y: T.number });

export const dimProps: Record<keyof DimProps, T.Validatable<any>> = {
  value: T.string.nullable(),
  direction: T.literalEnum("x", "y", "aligned"),
  offset: T.number.nullable(),
  chain: T.string.nullable(),
  refStart: DimRefStartStyle,
  refEnd: DimRefEndStyle,
  start: point,
  end: point,
};

const dimVersions = createShapePropsMigrationIds("dim", {
  AddRef: 1,
  SplitRefPerEnd: 2,
  AutoOffset: 3,
});

export const dimMigrations = createShapePropsMigrationSequence({
  sequence: [
    {
      id: dimVersions.AddRef,
      up(props) {
        props.ref = "stud";
      },
      down(props) {
        delete props.ref;
      },
    },
    {
      id: dimVersions.SplitRefPerEnd,
      up(props) {
        props.refStart = props.ref ?? "stud";
        props.refEnd = props.ref ?? "stud";
        delete props.ref;
      },
      down(props) {
        props.ref = props.refStart;
        delete props.refStart;
        delete props.refEnd;
      },
    },
    {
      // Every dim drawn before this stood off its span by a fixed distance,
      // which lands inside the plan as often as outside it. They all rejoin
      // the stack; a drag re-authors the one dim the drafter wants moved.
      id: dimVersions.AutoOffset,
      up(props) {
        props.offset = null;
      },
      down(props) {
        props.offset = props.offset ?? 24;
      },
    },
  ],
});

export type DimTerminal = "start" | "end";

/** A point on a target shape: normalized on its bounds, or the exact polyline
 *  vertex when the target has one (which survives edits to other vertices). */
export interface DimAnchor {
  anchor: { x: number; y: number };
  vertex?: string;
}

export interface DimBindingProps {
  terminal: DimTerminal;
  /** Normalized anchor on the target shape's bounds. */
  anchor: { x: number; y: number };
  /** For polyline targets: the point id this terminal measures, so the
   *  binding survives edits to unrelated vertices (bounds anchors do not). */
  vertex?: string;
  /** Set when this end measures to an *edge* rather than a point: the far end
   *  of that edge, in the same terms as `anchor`/`vertex`. The endpoint then
   *  resolves to the foot of the perpendicular dropped from the dim's other
   *  end, so a face-to-face dim measures across however the faces run. */
  edge?: DimAnchor;
}

declare module "@tldraw/tlschema" {
  interface TLGlobalBindingPropsMap {
    dim: DimBindingProps;
  }
}

export type DimBinding = TLBaseBinding<"dim", DimBindingProps>;

export const dimBindingProps: Record<
  keyof DimBindingProps,
  T.Validatable<any>
> = {
  terminal: T.literalEnum("start", "end"),
  anchor: point,
  vertex: T.string.optional(),
  edge: T.object({ anchor: point, vertex: T.string.optional() }).optional(),
};

/**
 * The binding id for a dim terminal is deterministic: one binding per
 * terminal is an identity rule, not a cleanup job, and every write path
 * (agent actions, UI tools) must mint the same id so a re-bind replaces
 * rather than accumulates.
 */
export function dimBindingId(dimId: string, terminal: DimTerminal): string {
  return `binding:${dimId.replace(/^shape:/, "")}-${terminal}`;
}
